import "./CreatePackage.scss";
import {
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  useMediaQuery,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import BlueButton from "../../../components/BlueButton/BlueButton";
import AddMore from "./../../../assets/add-more.svg";
import Delete from "./../../../assets/delete.svg";
import AddIcon from "@mui/icons-material/AddBoxOutlined";
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../../utils/api";
import { toast } from "react-toastify";
import Loader from "../../../components/Loader";

const CreateEditPackage = () => {
  const { packageId } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(packageId);
  const isMobile = useMediaQuery("(max-width:767px)");
  const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 1024px)");

  const [packageData, setPackageData] = useState({
    name: {
      ar: "",
      en: "",
    },
    description: {
      ar: "",
      en: "",
    },
    type: "annually",
    numberOfVisits: "",
    dailyMaximum: "",
    price: "",
    saving: "",
    list: "",
    popular: false,
  });

  // Updated features state with both ar and en properties
  const [features, setFeatures] = useState([
    {
      id: 0,
      ar: "",
      en: "",
    },
  ]);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lists, setLists] = useState([]);

  // Fetch existing package data if in edit mode
  useEffect(() => {
    if (isEditMode) {
      fetchPackageData();
    }
    fetchLists();
  }, [packageId, isEditMode]);

  const fetchLists = async () => {
    try {
      const response = await api.get("/package-lists");
      setLists(Array.isArray(response.data.data) ? response.data.data : []);
    } catch (error) {
      console.error("Error fetching lists:", error);
      toast.error("حدث خطأ أثناء تحميل القوائم");
      setLists([]);
    }
  };

  const fetchPackageData = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/packages/${packageId}`);
      const data = res.data.data;

      // Set the form data from API response
      setPackageData({
        name: {
          ar: data.name?.ar || "",
          en: data.name?.en || "",
        },
        description: {
          ar: data.description?.ar || "",
          en: data.description?.en || "",
        },
        type: data.type || "annually",
        numberOfVisits: data.numberOfVisits?.toString() || "",
        dailyMaximum: data.dailyMaximum?.toString() || "",
        price: data.price?.toString() || "",
        saving: data.saving || "",
        list: data.list || "",
        popular: data.popular || false,
      });

      // Set features - handle both old and new formats
      if (data.features && data.features.length > 0) {
        if (typeof data.features[0] === "string") {
          // Old format: convert strings to objects with ar property
          setFeatures(
            data.features.map((feature, index) => ({
              id: index,
              ar: feature,
              en: feature, // Default to same value for both languages
            }))
          );
        } else {
          // New format: already objects with ar and en
          setFeatures(
            data.features.map((feature, index) => ({
              id: index,
              ar: feature.ar || "",
              en: feature.en || "",
            }))
          );
        }
      }
    } catch (error) {
      console.error("Error fetching package data:", error);
      toast.error("Failed to fetch package data");
    } finally {
      setLoading(false);
    }
  };

  const addFeature = () => {
    const newId =
      features.length > 0 ? Math.max(...features.map((i) => i.id)) + 1 : 0;
    setFeatures([...features, { id: newId, ar: "", en: "" }]);
  };

  const deleteFeature = (id) => {
    setFeatures(features.filter((feature) => feature.id !== id));
  };

  // Updated to handle ar/en properties
  const handleFeatureChange = (id, lang, value) => {
    setFeatures(
      features.map((feature) =>
        feature.id === id ? { ...feature, [lang]: value } : feature
      )
    );
  };

  const handleInputChange = (field, value) => {
    setPackageData({
      ...packageData,
      [field]: value,
    });
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    console.log("Form submitted");

    // Validate form
    if (
      !packageData.name.ar ||
      !packageData.name.en ||
      !packageData.description.ar ||
      !packageData.description.en ||
      !packageData.numberOfVisits ||
      !packageData.dailyMaximum ||
      !packageData.price ||
      !packageData.list
    ) {
      toast.error("برجاء ملئ جميع الحقول");
      return;
    }

    // Filter out empty features
    const validFeatures = features.filter(
      (feature) => feature.ar.trim() !== "" && feature.en.trim() !== ""
    );
    if (validFeatures.length === 0) {
      toast.error("برجاء اضافة ميزة واحدة علي الاقل");
      return;
    }

    // Prepare data as JSON with updated features format
    const submitData = {
      name: {
        ar: packageData.name.ar,
        en: packageData.name.en,
      },
      description: {
        ar: packageData.description.ar,
        en: packageData.description.en,
      },
      type: packageData.type,
      numberOfVisits: parseInt(packageData.numberOfVisits),
      dailyMaximum: parseInt(packageData.dailyMaximum),
      price: parseFloat(packageData.price),
      saving: packageData.saving,
      list: packageData.list,
      popular: packageData.popular,
      features: validFeatures.map((feature) => ({
        ar: feature.ar.trim(),
        en: feature.en.trim(),
      })),
    };

    console.log("Submitting JSON data:", submitData);
    setSubmitting(true);

    try {
      let response;

      if (isEditMode) {
        // Update existing package
        response = await api.patch(`/packages/${packageId}`, submitData);
        toast.success("Package updated successfully");
      } else {
        // Create new package
        response = await api.post("/packages", submitData);
        toast.success("Package created successfully");
      }

      console.log("API response:", response);

      // Redirect to packages list after successful submission
      setTimeout(() => navigate("/packages"), 1500);
    } catch (error) {
      console.error("Error submitting package:", error);
      toast.error(
        error.response?.data?.message || "An error occurred during submission"
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && isEditMode) {
    return <Loader />;
  }

  return (
    <div className="create-product">
      <div className="special-header">
        <p className="title">
          {isEditMode ? "تعديل الباقة" : "اضافة باقة جديدة"}
        </p>
      </div>

      <div className="product-container">
        <form onSubmit={handleSubmit}>
          <h4>المعلومات الاساسية</h4>

          <div className="control-group">
            <FormControl sx={{ flex: "1" }} variant="outlined" required>
              <InputLabel
                htmlFor="package-name-ar"
                shrink
                sx={{
                  background: "white",
                  transformOrigin: "right top",
                  transform: "translate(14px, -9px) scale(0.8)",
                  right: 24,
                  left: "auto",
                  paddingLeft: "4px",
                  paddingRight: "4px",
                  fontWeight: "bold",
                }}
              >
                اسم الباقة
              </InputLabel>
              <OutlinedInput
                sx={{ borderRadius: "8px" }}
                required
                id="package-name-ar"
                type="text"
                notched
                value={packageData.name.ar}
                onChange={(e) =>
                  handleInputChange("name", {
                    ...packageData.name,
                    ar: e.target.value,
                  })
                }
              />
            </FormControl>

            <FormControl sx={{ flex: "1" }} variant="outlined" required>
              <InputLabel
                htmlFor="package-name-en"
                shrink
                sx={{
                  background: "white",
                  transformOrigin: "right top",
                  transform: "translate(14px, -9px) scale(0.8)",
                  right: 24,
                  left: "auto",
                  paddingLeft: "4px",
                  paddingRight: "4px",
                  fontWeight: "bold",
                }}
              >
                الاسم باللغة الانجليزية
              </InputLabel>
              <OutlinedInput
                sx={{ borderRadius: "8px" }}
                required
                id="package-name-en"
                type="text"
                notched
                value={packageData.name.en}
                onChange={(e) =>
                  handleInputChange("name", {
                    ...packageData.name,
                    en: e.target.value,
                  })
                }
              />
            </FormControl>
          </div>

          {/* Description fields */}
          <div className="control-group">
            <FormControl sx={{ flex: "1" }} variant="outlined" required>
              <InputLabel
                htmlFor="package-description-ar"
                shrink
                sx={{
                  background: "white",
                  transformOrigin: "right top",
                  transform: "translate(14px, -9px) scale(0.8)",
                  right: 24,
                  left: "auto",
                  paddingLeft: "4px",
                  paddingRight: "4px",
                  fontWeight: "bold",
                }}
              >
                وصف الباقة
              </InputLabel>
              <OutlinedInput
                sx={{ borderRadius: "8px" }}
                required
                id="package-description-ar"
                type="text"
                multiline
                rows={3}
                notched
                value={packageData.description.ar}
                onChange={(e) =>
                  handleInputChange("description", {
                    ...packageData.description,
                    ar: e.target.value,
                  })
                }
              />
            </FormControl>

            <FormControl sx={{ flex: "1" }} variant="outlined" required>
              <InputLabel
                htmlFor="package-description-en"
                shrink
                sx={{
                  background: "white",
                  transformOrigin: "right top",
                  transform: "translate(14px, -9px) scale(0.8)",
                  right: 24,
                  left: "auto",
                  paddingLeft: "4px",
                  paddingRight: "4px",
                  fontWeight: "bold",
                }}
              >
                الوصف باللغة الانجليزية
              </InputLabel>
              <OutlinedInput
                sx={{ borderRadius: "8px" }}
                required
                id="package-description-en"
                type="text"
                multiline
                rows={3}
                notched
                value={packageData.description.en}
                onChange={(e) =>
                  handleInputChange("description", {
                    ...packageData.description,
                    en: e.target.value,
                  })
                }
              />
            </FormControl>
          </div>

          <div className="control-group">
            <FormControl sx={{ flex: "1" }} variant="outlined" required>
              <InputLabel
                htmlFor="subscription-type"
                shrink
                sx={{
                  background: "white",
                  transformOrigin: "right top",
                  transform: "translate(14px, -9px) scale(0.8)",
                  right: 24,
                  left: "auto",
                  paddingLeft: "4px",
                  paddingRight: "4px",
                  fontWeight: "bold",
                }}
              >
                نوع الاشتراك
              </InputLabel>
              <Select
                id="subscription-type"
                sx={{ borderRadius: "8px" }}
                value={packageData.type}
                onChange={(e) => handleInputChange("type", e.target.value)}
                input={<OutlinedInput notched />}
              >
                <MenuItem key="annually" value="annually">
                  سنوية
                </MenuItem>
                <MenuItem key="monthly" value="monthly">
                  شهرية
                </MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ flex: "1" }} variant="outlined" required>
              <InputLabel
                htmlFor="package-price"
                shrink
                sx={{
                  background: "white",
                  transformOrigin: "right top",
                  transform: "translate(14px, -9px) scale(0.8)",
                  right: 24,
                  left: "auto",
                  paddingLeft: "4px",
                  paddingRight: "4px",
                  fontWeight: "bold",
                }}
              >
                سعر الباقة
              </InputLabel>
              <OutlinedInput
                sx={{ borderRadius: "8px" }}
                id="package-price"
                type="text"
                placeholder="ريال"
                notched
                value={packageData.price}
                onChange={(e) => handleInputChange("price", e.target.value)}
              />
            </FormControl>
          </div>

          <div className="control-group">
            <FormControl sx={{ flex: "1" }} variant="outlined" required>
              <InputLabel
                htmlFor="visits-count"
                shrink
                sx={{
                  background: "white",
                  transformOrigin: "right top",
                  transform: "translate(14px, -9px) scale(0.8)",
                  right: 24,
                  left: "auto",
                  paddingLeft: "4px",
                  paddingRight: "4px",
                  fontWeight: "bold",
                }}
              >
                عدد الزيارات
              </InputLabel>
              <OutlinedInput
                sx={{ borderRadius: "8px" }}
                id="visits-count"
                type="text"
                notched
                value={packageData.numberOfVisits}
                onChange={(e) =>
                  handleInputChange("numberOfVisits", e.target.value)
                }
              />
            </FormControl>

            <FormControl sx={{ flex: "1" }} variant="outlined" required>
              <InputLabel
                htmlFor="daily-maximum"
                shrink
                sx={{
                  background: "white",
                  transformOrigin: "right top",
                  transform: "translate(14px, -9px) scale(0.8)",
                  right: 24,
                  left: "auto",
                  paddingLeft: "4px",
                  paddingRight: "4px",
                  fontWeight: "bold",
                }}
              >
                الحد الأقصى اليومي
              </InputLabel>
              <OutlinedInput
                sx={{ borderRadius: "8px" }}
                id="daily-maximum"
                type="text"
                notched
                value={packageData.dailyMaximum}
                onChange={(e) =>
                  handleInputChange("dailyMaximum", e.target.value)
                }
              />
            </FormControl>
          </div>

          <div className="control-group">
            <FormControl sx={{ flex: "1" }} variant="outlined">
              <InputLabel
                htmlFor="package-saving"
                shrink
                sx={{
                  background: "white",
                  transformOrigin: "right top",
                  transform: "translate(14px, -9px) scale(0.8)",
                  right: 24,
                  left: "auto",
                  paddingLeft: "4px",
                  paddingRight: "4px",
                  fontWeight: "bold",
                }}
              >
                وفر
              </InputLabel>
              <OutlinedInput
                sx={{ borderRadius: "8px" }}
                id="package-saving"
                type="text"
                placeholder="مثال: 20 ريال"
                notched
                value={packageData.saving}
                onChange={(e) => handleInputChange("saving", e.target.value)}
              />
            </FormControl>
            <FormControl sx={{ flex: "1" }} variant="outlined" required>
              <InputLabel
                htmlFor="package-list"
                shrink
                sx={{
                  background: "white",
                  transformOrigin: "right top",
                  transform: "translate(14px, -9px) scale(0.8)",
                  right: 24,
                  left: "auto",
                  paddingLeft: "4px",
                  paddingRight: "4px",
                  fontWeight: "bold",
                }}
              >
                القائمة
              </InputLabel>
              <Select
                id="package-list"
                sx={{ borderRadius: "8px" }}
                value={packageData.list}
                onChange={(e) => handleInputChange("list", e.target.value)}
                input={<OutlinedInput notched />}
              >
                {lists.map((list) => (
                  <MenuItem key={list._id} value={list._id}>
                    {list.name.ar}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <div className="control-group">
            <FormControl sx={{ flex: "1" }} variant="outlined">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={packageData.popular}
                    onChange={(e) =>
                      handleInputChange("popular", e.target.checked)
                    }
                    name="popular"
                  />
                }
                label="الاكثر شيوعاً"
                sx={{
                  "& .MuiFormControlLabel-label": {
                    fontFamily: "Cairo, sans-serif",
                  },
                }}
              />
            </FormControl>
          </div>

          <div className="control-group">
            <div className="item" style={{ marginTop: "32px" }}>
              <h4>المميزات</h4>
            </div>
            <div className="description-container">
              {features.map((feature) => (
                <div key={feature.id} className="desc-item">
                  {/* Arabic feature */}
                  <FormControl sx={{ flex: "1" }} variant="outlined" required>
                    <InputLabel
                      htmlFor={`feature-ar-${feature.id}`}
                      shrink
                      sx={{
                        background: "white",
                        transformOrigin: "right top",
                        transform: "translate(14px, -9px) scale(0.8)",
                        right: 24,
                        left: "auto",
                        paddingLeft: "4px",
                        paddingRight: "4px",
                        fontWeight: "bold",
                      }}
                    >
                      وصف الميزة
                    </InputLabel>
                    <OutlinedInput
                      sx={{ borderRadius: "8px" }}
                      id={`feature-ar-${feature.id}`}
                      type="text"
                      notched
                      value={feature.ar}
                      onChange={(e) =>
                        handleFeatureChange(feature.id, "ar", e.target.value)
                      }
                    />
                  </FormControl>

                  {/* English feature */}
                  <FormControl sx={{ flex: "1" }} variant="outlined" required>
                    <InputLabel
                      htmlFor={`feature-en-${feature.id}`}
                      shrink
                      sx={{
                        background: "white",
                        transformOrigin: "right top",
                        transform: "translate(14px, -9px) scale(0.8)",
                        right: 24,
                        left: "auto",
                        paddingLeft: "4px",
                        paddingRight: "4px",
                        fontWeight: "bold",
                      }}
                    >
                      وصف الميزة بالانجليزية
                    </InputLabel>
                    <OutlinedInput
                      sx={{ borderRadius: "8px" }}
                      id={`feature-en-${feature.id}`}
                      type="text"
                      notched
                      value={feature.en}
                      onChange={(e) =>
                        handleFeatureChange(feature.id, "en", e.target.value)
                      }
                    />
                  </FormControl>

                  {isMobile ? (
                    <AddIcon className="icon" onClick={addFeature} />
                  ) : (
                    <img src={AddMore} alt="" onClick={addFeature} />
                  )}
                  {feature.id !== 0 && (
                    <img
                      src={Delete}
                      alt=""
                      onClick={() => deleteFeature(feature.id)}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="button-container" style={{ marginTop: "24px" }}>
            <BlueButton
              text={isEditMode ? "تحديث" : "اضافة"}
              size={isMobile ? "full" : isTablet ? "large" : "large"}
              variant="fill"
              disabled={submitting}
              onClick={() => {
                if (!submitting) {
                  handleSubmit();
                }
              }}
              loading={submitting}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateEditPackage;
